import React, { useEffect, useState, useRef } from 'react';

// Animated number counter
export function AnimatedNumber({ value, prefix = '', suffix = '', duration = 800 }) {
  const [display, setDisplay] = useState(0);
  const startRef = useRef();
  useEffect(() => {
    const start = display;
    const target = Number(value) || 0;
    const t0 = Date.now();
    let raf;
    const tick = () => {
      const t = Math.min(1, (Date.now() - t0) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(start + (target - start) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    tick();
    return () => raf && cancelAnimationFrame(raf);
  }, [value]);
  const v = display % 1 === 0 ? Math.round(display) : display.toFixed(2);
  return <span>{prefix}{v}{suffix}</span>;
}

// Avatar with initials + gradient
export function Avatar({ name, size = 36, frame = null }) {
  const initials = (name || '').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase() || '?';
  const hue = [...name].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  return (
    <div className={`dh-avatar ${frame ? 'dh-avatar-frame-' + frame : ''}`}
      style={{
        width: size, height: size, fontSize: size * 0.4,
        background: `linear-gradient(135deg, hsl(${hue}, 70%, 55%), hsl(${(hue+60)%360}, 70%, 45%))`
      }}>
      {initials}
    </div>
  );
}

// Skeleton loader
export function Skeleton({ count = 3 }) {
  return (
    <div style={{ padding: 16 }}>
      {Array.from({ length: count }).map((_, i) => <div key={i} className="dh-skeleton dh-skeleton-card" />)}
    </div>
  );
}

// Custom poker chip refresh spinner
export function ChipSpinner() {
  return <div className="dh-chip-spinner" />;
}

// SVG sparkline chart
export function Sparkline({ data, width = 200, height = 50 }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const points = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * height}`).join(' ');
  const fillPoints = `0,${height} ${points} ${width},${height}`;
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polygon className="dh-sparkline-fill" points={fillPoints} />
      <polyline className="dh-sparkline" points={points} />
    </svg>
  );
}

// GitHub-style heatmap (last 90 days)
export function ContributionHeatmap({ shifts }) {
  const today = new Date(); today.setHours(0,0,0,0);
  const days = [];
  for (let i = 89; i >= 0; i--) {
    const d = new Date(today); d.setDate(d.getDate() - i);
    const ds = d.toISOString().split('T')[0];
    const count = shifts.filter(s => new Date(s.startTime || s.start_time).toISOString().split('T')[0] === ds).length;
    days.push({ date: ds, count });
  }
  const lvl = (n) => n === 0 ? '' : n === 1 ? 'lvl1' : n === 2 ? 'lvl2' : n === 3 ? 'lvl3' : 'lvl4';
  return (
    <div className="dh-heatmap">
      {days.map(d => (
        <div key={d.date} className={`dh-heatmap-cell ${lvl(d.count)}`} title={`${d.date}: ${d.count} shifts`} />
      ))}
    </div>
  );
}

// Confetti burst
export function fireConfetti() {
  const colors = ['#dc2626', '#fcd34d', '#16a34a', '#2563eb', '#7c3aed', '#db2777'];
  for (let i = 0; i < 50; i++) {
    const piece = document.createElement('div');
    piece.className = 'dh-confetti-piece';
    piece.style.left = Math.random() * 100 + 'vw';
    piece.style.background = colors[Math.floor(Math.random() * colors.length)];
    piece.style.animationDelay = Math.random() * 0.3 + 's';
    piece.style.transform = `rotate(${Math.random() * 360}deg)`;
    if (Math.random() > 0.5) piece.style.borderRadius = '50%';
    document.body.appendChild(piece);
    setTimeout(() => piece.remove(), 3500);
  }
}

// Compute streak from shifts
export function computeStreak(shifts) {
  if (!shifts || shifts.length === 0) return 0;
  const completed = shifts
    .filter(s => s.checkedInAt)
    .map(s => new Date(s.startTime || s.start_time).toISOString().split('T')[0])
    .sort()
    .reverse();
  if (completed.length === 0) return 0;
  let streak = 1;
  for (let i = 0; i < completed.length - 1; i++) {
    const a = new Date(completed[i]);
    const b = new Date(completed[i + 1]);
    const diff = Math.round((a - b) / 86400000);
    if (diff === 1) streak++;
    else if (diff > 1) break;
  }
  return streak;
}

// XP / level system
export function computeLevel(stats) {
  const games = parseInt(stats?.games_total || 0);
  const xp = games * 10 + parseInt(stats?.checkins_total || 0) * 5;
  const level = Math.floor(Math.sqrt(xp / 50)) + 1;
  const nextLevelXp = Math.pow(level, 2) * 50;
  const prevLevelXp = Math.pow(level - 1, 2) * 50;
  const progress = Math.round(((xp - prevLevelXp) / (nextLevelXp - prevLevelXp)) * 100);
  const tier = level >= 20 ? 'platinum' : level >= 10 ? 'gold' : level >= 5 ? 'silver' : 'bronze';
  return { xp, level, nextLevelXp, progress, tier };
}

// Achievements
export function computeAchievements(stats, shifts, streak) {
  const achievements = [];
  const games = parseInt(stats?.games_total || 0);
  const checkins = parseInt(stats?.checkins_total || 0);
  if (games >= 1) achievements.push({ id: 'first', icon: '🎲', name: 'First Game', desc: 'Dealt your first game' });
  if (games >= 10) achievements.push({ id: '10g', icon: '🎯', name: '10 Games', desc: 'Dealt 10 games' });
  if (games >= 100) achievements.push({ id: '100g', icon: '💯', name: 'Century', desc: 'Dealt 100 games' });
  if (games >= 500) achievements.push({ id: '500g', icon: '🏆', name: 'Veteran', desc: 'Dealt 500 games' });
  if (checkins >= 1) achievements.push({ id: 'checkin', icon: '📍', name: 'Checked In', desc: 'First check-in' });
  if (streak >= 7) achievements.push({ id: 'week', icon: '🔥', name: 'Week Streak', desc: '7-day streak' });
  if (streak >= 30) achievements.push({ id: 'month', icon: '🌟', name: 'Month Master', desc: '30-day streak' });
  return achievements;
}

// Time-of-day greeting
export function getGreeting(name) {
  const h = new Date().getHours();
  const part = h < 12 ? 'morning' : h < 18 ? 'afternoon' : 'evening';
  return `Good ${part}, ${name}`;
}
